export interface PdfRenderBudget {
  /** Hard ceiling on canvas pixel area (width multiplied by height). */
  maxArea: number;
  /** Hard ceiling on either canvas dimension. */
  maxDimension: number;
  /** Highest default scale factor applied to the PDF's natural size. */
  maxScale: number;
}

const isAppleMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && (navigator.maxTouchPoints ?? 0) > 1)
  );
};

const isCoarsePointer = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(pointer: coarse)').matches;
};

export const getPdfRenderBudget = (): PdfRenderBudget => {
  // Every browser on iOS uses WebKit and shares the same constrained content-
  // process memory model. Do not give Chrome/Firefox/Edge on iOS the looser
  // generic tablet budget just because their user-agent tokens differ.
  if (isAppleMobileDevice()) {
    return { maxArea: 4_000_000, maxDimension: 3072, maxScale: 1 };
  }
  if (isCoarsePointer()) {
    return { maxArea: 6_000_000, maxDimension: 3072, maxScale: 1.5 };
  }
  return { maxArea: 33_000_000, maxDimension: 8192, maxScale: 2 };
};

/**
 * Constrained/touch devices should prefer a server-rendered PDF raster when
 * the host can provide one. This avoids parsing a complex vector PDF and
 * allocating its canvas inside the same browser process as the viewer.
 */
export const shouldUsePdfRasterFallback = (): boolean =>
  isAppleMobileDevice() || isCoarsePointer();

/**
 * Select a render scale that preserves the requested resolution where
 * possible while respecting browser canvas-area and dimension limits.
 *
 * `ignoreScaleCap` permits zoom-driven renders above the normal device scale
 * cap; crash-protection area and dimension limits always remain active.
 */
export const pickSafePdfRenderScale = (
  naturalWidth: number,
  naturalHeight: number,
  desiredScale = 2,
  options?: { ignoreScaleCap?: boolean },
): number => {
  if (!Number.isFinite(naturalWidth) || naturalWidth <= 0) return desiredScale;
  if (!Number.isFinite(naturalHeight) || naturalHeight <= 0) return desiredScale;

  const budget = getPdfRenderBudget();
  let scale = options?.ignoreScaleCap
    ? desiredScale
    : Math.min(desiredScale, budget.maxScale);

  const areaScale = Math.sqrt(budget.maxArea / (naturalWidth * naturalHeight));
  scale = Math.min(scale, areaScale);

  const widthScale = budget.maxDimension / naturalWidth;
  const heightScale = budget.maxDimension / naturalHeight;
  scale = Math.min(scale, widthScale, heightScale);

  // Do not impose a minimum scale here. Very large PDF pages can require a
  // scale below 0.5 to stay within the canvas limits above. Raising the scale
  // after applying those limits defeats the crash guard and can cause iOS
  // WebKit to terminate the entire content process instead of throwing a
  // catchable JavaScript error.
  return scale;
};

/** Compatibility name used by the Ironview 360 viewer during extraction. */
export const pickSafeRenderScale = pickSafePdfRenderScale;
